import * as React from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance, tokenBridgeUserReady, type TokenBridgeUser } from "./AuthProvider";
import { armTokenRequest, loginRequest, ARM_TENANTS_URL } from "./msalConfig";
import { primer } from "../styles/primerDarkTheme";

const useLocalTokenBridge = import.meta.env.VITE_USE_LOCAL_TOKEN_BRIDGE === "true";

// ── Types ─────────────────────────────────────────────────────────────────────

interface TenantInfo {
    tenantId: string;
    displayName?: string;
}

type DiscoveryState =
    | { status: "loading" }
    | { status: "needs_interaction" }
    | { status: "picking"; tenants: TenantInfo[] }
    | { status: "done"; tenantId: string }
    | { status: "no_org" };

interface TenantContextValue {
    selectedTenantId: string | null;
    selectedTenantName: string | null;
    availableTenants: TenantInfo[];
    triggerMsaDiscovery(): void;
    switchOrg(): void;
    switchDirectory(): void;
    selectTenant(tenantId: string): void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const TENANT_KEY = (homeAccountId: string) => `dashboard.tenantId.${homeAccountId}`;

/** Extract the home tenant ID from MSAL's homeAccountId (format: "{uid}.{utid}"). */
function getHomeTenantId(homeAccountId: string): string | null {
    const parts = homeAccountId.split(".");
    return parts.length >= 2 ? parts[1] : null;
}

interface TenantsResponse {
    value: { tenantId: string; displayName?: string }[];
}

async function fetchTenants(accessToken: string): Promise<TenantInfo[]> {
    const resp = await fetch(ARM_TENANTS_URL, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!resp.ok) return [];
    const body = (await resp.json()) as TenantsResponse;
    console.log("[TenantContext] GET /tenants response:", body);
    return (body.value ?? []).map(t => ({ tenantId: t.tenantId, displayName: t.displayName }));
}

// ── Context ───────────────────────────────────────────────────────────────────

const TenantContext = React.createContext<TenantContextValue>({
    selectedTenantId: null,
    selectedTenantName: null,
    availableTenants: [],
    triggerMsaDiscovery: () => void 0,
    switchOrg: () => void 0,
    switchDirectory: () => void 0,
    selectTenant: () => void 0,
});

export const useTenant = () => React.useContext(TenantContext);

// ── Provider ──────────────────────────────────────────────────────────────────

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { accounts } = useMsal();

    const BRIDGE_TENANT_KEY = "dashboard.bridge.tenantId";
    const [bridgeTenantId, setBridgeTenantId] = React.useState<string | null>(
        () => localStorage.getItem(BRIDGE_TENANT_KEY),
    );
    const [bridgeTenants, setBridgeTenants] = React.useState<TenantInfo[]>([]);

    React.useEffect(() => {
        if (!useLocalTokenBridge) return;
        void tokenBridgeUserReady.then((user: TokenBridgeUser | null) => {
            // Only set from the bridge user if no persisted selection exists
            if (!localStorage.getItem(BRIDGE_TENANT_KEY) && user?.tenantId) {
                setBridgeTenantId(user.tenantId);
                localStorage.setItem(BRIDGE_TENANT_KEY, user.tenantId);
            }
        });
        // Fetch tenant list via a separate ARM token from the bridge
        const bridgeBase = (import.meta.env.VITE_LOCAL_TOKEN_BRIDGE_URL || "http://localhost:3031/token").replace(/\/token$/, "");
        void fetch(`${bridgeBase}/arm-token`)
            .then(res => res.ok ? res.json() : null)
            .then(async (body: { accessToken?: string } | null) => {
                if (!body?.accessToken) return;
                const tenants = await fetchTenants(body.accessToken);
                setBridgeTenants(tenants);
            })
            .catch(() => { /* non-critical */ });
    }, []);

    const bridgeTenantName = bridgeTenantId
        ? bridgeTenants.find(t => t.tenantId === bridgeTenantId)?.displayName ?? null
        : null;

    const bridgeSelectTenant = React.useCallback((tid: string) => {
        setBridgeTenantId(tid);
        localStorage.setItem(BRIDGE_TENANT_KEY, tid);
    }, []);

    if (useLocalTokenBridge) {
        return (
            <TenantContext.Provider value={{
                selectedTenantId: bridgeTenantId,
                selectedTenantName: bridgeTenantName,
                availableTenants: bridgeTenants,
                triggerMsaDiscovery: () => void 0,
                switchOrg: () => void 0,
                switchDirectory: () => void 0,
                selectTenant: bridgeSelectTenant,
            }}>
                {children}
            </TenantContext.Provider>
        );
    }

    return <MsalTenantProvider accounts={accounts}>{children}</MsalTenantProvider>;
};

// ── MSAL-based provider (same flow for ALL account types) ─────────────────────

const MsalTenantProvider: React.FC<{
    accounts: ReturnType<typeof useMsal>["accounts"];
    children: React.ReactNode;
}> = ({ accounts, children }) => {
    const account = msalInstance.getActiveAccount() ?? accounts[0] ?? null;
    const tenantKey = account ? TENANT_KEY(account.homeAccountId) : null;
    const homeTenantId = account ? getHomeTenantId(account.homeAccountId) : null;

    // When true, the user explicitly requested a directory switch — show picker instead of auto-selecting.
    const explicitSwitchRef = React.useRef(false);

    // Keep track of all fetched tenants so the Header can display the directory list
    const [allTenants, setAllTenants] = React.useState<TenantInfo[]>([]);

    const [state, setState] = React.useState<DiscoveryState>(() => {
        const cached = tenantKey ? localStorage.getItem(tenantKey) : null;
        if (cached) return { status: "done", tenantId: cached };
        return { status: "loading" };
    });

    /**
     * Resolve tenants after fetching: auto-select home tenant on first login,
     * show picker on explicit switch, fall back to picker if home tenant not found.
     */
    const resolveTenants = React.useCallback((tenants: TenantInfo[]) => {
        setAllTenants(tenants);
        if (tenants.length === 0) {
            setState({ status: "no_org" });
            return;
        }
        if (tenants.length === 1) {
            const tid = tenants[0].tenantId;
            if (tenantKey) localStorage.setItem(tenantKey, tid);
            setState({ status: "done", tenantId: tid });
            return;
        }
        // Multiple tenants
        if (!explicitSwitchRef.current && homeTenantId) {
            const homeTenant = tenants.find(t => t.tenantId === homeTenantId);
            if (homeTenant) {
                if (tenantKey) localStorage.setItem(tenantKey, homeTenant.tenantId);
                setState({ status: "done", tenantId: homeTenant.tenantId });
                return;
            }
        }
        // Explicit switch or home tenant not in list — show picker
        explicitSwitchRef.current = false;
        setState({ status: "picking", tenants });
    }, [tenantKey, homeTenantId]);

    // Step 1: POST /organizations/token (silent) → GET /tenants
    React.useEffect(() => {
        if (!account) return;
        if (state.status !== "loading") return;

        void msalInstance.acquireTokenSilent({ ...armTokenRequest, account })
            .then(async tokenResponse => {
                const tenants = await fetchTenants(tokenResponse.accessToken);
                resolveTenants(tenants);
            })
            .catch((err) => {
                if (err instanceof InteractionRequiredAuthError) {
                    setState({ status: "needs_interaction" });
                } else {
                    console.warn("[TenantContext] Silent ARM token failed:", err);
                    setState({ status: "needs_interaction" });
                }
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account?.homeAccountId]);

    // Fetch tenant list in background when we already have a cached selection
    // so the directory name and switch-directory list are available.
    React.useEffect(() => {
        if (!account) return;
        if (allTenants.length > 0) return; // already loaded
        if (state.status !== "done") return; // only when cached

        void msalInstance.acquireTokenSilent({ ...armTokenRequest, account })
            .then(async tokenResponse => {
                const tenants = await fetchTenants(tokenResponse.accessToken);
                setAllTenants(tenants);
            })
            .catch(() => { /* non-critical — menu just won't show tenant name */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account?.homeAccountId, state.status]);

    const triggerMsaDiscovery = React.useCallback(() => {
        if (!account) return;
        setState({ status: "loading" });
        void msalInstance.acquireTokenPopup({
            ...armTokenRequest,
            loginHint: account.username,
        }).then(async tokenResponse => {
            const tenants = await fetchTenants(tokenResponse.accessToken);
            resolveTenants(tenants);
        }).catch(() => {
            setState({ status: "needs_interaction" });
        });
    }, [account, resolveTenants]);

    const switchOrg = React.useCallback(() => {
        Object.keys(localStorage)
            .filter(k => k.startsWith("dashboard.tenantId."))
            .forEach(k => localStorage.removeItem(k));
        void msalInstance.clearCache().then(() => {
            void msalInstance.loginRedirect({ ...loginRequest, prompt: "select_account" });
        });
    }, []);

    const switchDirectory = React.useCallback(() => {
        if (tenantKey) localStorage.removeItem(tenantKey);
        explicitSwitchRef.current = true;
        setState({ status: "loading" });
    }, [tenantKey]);

    const selectTenant = React.useCallback((tid: string) => {
        if (tenantKey) localStorage.setItem(tenantKey, tid);
        setState({ status: "done", tenantId: tid });
    }, [tenantKey]);

    const selectedTenantId = state.status === "done" ? state.tenantId : null;
    const selectedTenantName = selectedTenantId
        ? allTenants.find(t => t.tenantId === selectedTenantId)?.displayName ?? null
        : null;
    const value = React.useMemo(
        () => ({ selectedTenantId, selectedTenantName, availableTenants: allTenants, triggerMsaDiscovery, switchOrg, switchDirectory, selectTenant }),
        [selectedTenantId, selectedTenantName, allTenants, triggerMsaDiscovery, switchOrg, switchDirectory, selectTenant],
    );

    if (state.status === "loading") {
        return (
            <TenantContext.Provider value={value}>
                <DiscoveryScreen status="loading" onConnect={triggerMsaDiscovery} onSwitch={switchOrg} />
            </TenantContext.Provider>
        );
    }

    if (state.status === "needs_interaction") {
        return (
            <TenantContext.Provider value={value}>
                <DiscoveryScreen status="needs_interaction" onConnect={triggerMsaDiscovery} onSwitch={switchOrg} />
            </TenantContext.Provider>
        );
    }

    if (state.status === "no_org") {
        return (
            <TenantContext.Provider value={value}>
                <DiscoveryScreen status="no_org" onConnect={triggerMsaDiscovery} onSwitch={switchOrg} />
            </TenantContext.Provider>
        );
    }

    if (state.status === "picking") {
        return (
            <TenantContext.Provider value={value}>
                <TenantPickerScreen tenants={state.tenants} onSelect={selectTenant} onSwitch={switchOrg} />
            </TenantContext.Provider>
        );
    }

    return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

// ── Discovery / loading screen ────────────────────────────────────────────────

const DiscoveryScreen: React.FC<{
    status: "loading" | "needs_interaction" | "no_org";
    onConnect(): void;
    onSwitch(): void;
}> = ({ status, onConnect, onSwitch }) => (
    <div style={screenStyle}>
        <img src="/icons/app-icon.svg" alt="" style={{ width: 40, height: 40, opacity: 0.9 }} />
        <p style={{ color: primer.fg, fontSize: 18, fontWeight: 600, margin: 0 }}>
            Testing Agent Dashboard
        </p>
        {status === "loading" && (
            <p style={{ color: primer.fgMuted, fontSize: 14, margin: 0 }}>Signing you in…</p>
        )}
        {status === "needs_interaction" && (
            <>
                <p style={{ color: primer.fgMuted, fontSize: 14, margin: 0, maxWidth: 480, textAlign: "center" }}>
                    We need your consent to discover your Azure organizations.
                </p>
                <button onClick={onConnect} style={btn(primer.blue)}>
                    Grant access
                </button>
                <button onClick={onSwitch} style={btn("transparent", primer.fgMuted)}>
                    Sign in with a different account
                </button>
            </>
        )}
        {status === "no_org" && (
            <>
                <p style={{ color: primer.fgMuted, fontSize: 14, margin: 0, maxWidth: 480, textAlign: "center" }}>
                    Your account is not associated with any Azure organization.
                </p>
                <button onClick={onSwitch} style={btn(primer.blue)}>
                    Sign in with a different account
                </button>
            </>
        )}
    </div>
);

// ── Tenant picker screen ──────────────────────────────────────────────────────

const TenantPickerScreen: React.FC<{
    tenants: TenantInfo[];
    onSelect(tenantId: string): void;
    onSwitch(): void;
}> = ({ tenants, onSelect, onSwitch }) => (
    <div style={screenStyle}>
        <img src="/icons/app-icon.svg" alt="" style={{ width: 40, height: 40, opacity: 0.9 }} />
        <p style={{ color: primer.fg, fontSize: 18, fontWeight: 600, margin: 0 }}>
            Select a directory
        </p>
        <p style={{ color: primer.fgMuted, fontSize: 14, margin: 0 }}>
            Choose the Azure directory you want to work in.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", maxWidth: 420, marginTop: 8 }}>
            {tenants.map(t => (
                <button
                    key={t.tenantId}
                    onClick={() => onSelect(t.tenantId)}
                    style={{
                        display: "flex", flexDirection: "column", gap: 2,
                        padding: "12px 16px", borderRadius: 8,
                        border: `1px solid ${primer.border}`,
                        background: primer.surface, color: primer.fg,
                        cursor: "pointer", textAlign: "left", fontSize: 14, fontWeight: 500,
                    }}
                >
                    <span>{t.displayName || "Directory"}</span>
                    <span style={{ fontSize: 12, color: primer.fgMuted, fontWeight: 400 }}>{t.tenantId}</span>
                </button>
            ))}
        </div>
        <button onClick={onSwitch} style={{ ...btn("transparent", primer.fgMuted), marginTop: 8 }}>
            Sign in with a different account
        </button>
    </div>
);

// ── Shared styles ─────────────────────────────────────────────────────────────

const screenStyle: React.CSSProperties = {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", height: "100vh", gap: 16,
    backgroundColor: primer.canvas, fontFamily: "sans-serif",
};

function btn(bg: string, color = "#fff"): React.CSSProperties {
    return {
        padding: "10px 24px", borderRadius: 6,
        border: `1px solid ${primer.border}`,
        background: bg, color, cursor: "pointer", fontSize: 14, fontWeight: 500,
    };
}
